"use client";

import { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";

// Extract type from embla-carousel-react to avoid installing base package
type EmblaCarouselType = ReturnType<typeof useEmblaCarousel>[1];

/**
 * Hook to manage carousel scroll state (canScrollPrev, canScrollNext)
 */
export function useCarouselScroll(api: EmblaCarouselType | undefined) {
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback((emblaApi: EmblaCarouselType | undefined) => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!api) return;

    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api, onSelect]);

  const scrollPrev = useCallback(() => api?.scrollPrev(), [api]);
  const scrollNext = useCallback(() => api?.scrollNext(), [api]);

  return {
    canScrollPrev,
    canScrollNext,
    scrollPrev,
    scrollNext,
  };
}

/**
 * Hook to track current slide index
 */
export function useCarouselIndex(api: EmblaCarouselType | undefined) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType | undefined) => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  const onInit = useCallback((emblaApi: EmblaCarouselType | undefined) => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  useEffect(() => {
    if (!api) return;

    onInit(api);
    onSelect(api);
    api.on("reInit", onInit);
    api.on("reInit", onSelect);
    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
      api.off("reInit", onInit);
      api.off("reInit", onSelect);
    };
  }, [api, onInit, onSelect]);

  const scrollTo = useCallback((index: number) => api?.scrollTo(index), [api]);

  return {
    selectedIndex,
    scrollSnaps,
    scrollTo,
    totalSlides: scrollSnaps.length,
  };
}

/**
 * Hook for carousel progress (useful for story-like carousels)
 */
export function useCarouselProgress(api: EmblaCarouselType | undefined) {
  const [scrollProgress, setScrollProgress] = useState(0);

  const onScroll = useCallback((emblaApi: EmblaCarouselType | undefined) => {
    if (!emblaApi) return;
    const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()));
    setScrollProgress(progress * 100);
  }, []);

  useEffect(() => {
    if (!api) return;

    onScroll(api);
    api.on("scroll", onScroll);
    api.on("reInit", onScroll);

    return () => {
      api.off("scroll", onScroll);
      api.off("reInit", onScroll);
    };
  }, [api, onScroll]);

  return scrollProgress;
}

/**
 * Hook for keyboard navigation in carousels
 */
export function useCarouselKeyboard(
  api: EmblaCarouselType | undefined,
  enabled: boolean = true
) {
  useEffect(() => {
    if (!api || !enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // For RTL: ArrowLeft goes next, ArrowRight goes prev
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        api.scrollNext();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        api.scrollPrev();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [api, enabled]);
}
